import { unitText } from './document.mjs'

// Broad historical periods are intervals, not exact event dates.
const periods = [
  // Local approximate convention; retain the original mention alongside this interval.
  ['唐末', 875, 907, 'approximate'],
  ['唐朝末年', 875, 907, 'approximate'],
  ['唐代末期', 875, 907, 'approximate'],
  ['唐代', 618, 907],
  ['唐朝', 618, 907],
  ['五代', 907, 960],
  ['北宋', 960, 1127],
  ['南宋', 1127, 1279],
  ['宋代', 960, 1279],
  ['宋朝', 960, 1279],
  ['两宋', 960, 1279],
  ['明代', 1368, 1644],
  ['明朝', 1368, 1644],
  ['清代', 1644, 1912],
  ['清朝', 1644, 1912],
  ['明清', 1368, 1912],
]
const numeric = (text) => {
  if (/^\d+$/.test(text)) return Number(text)
  const digits = { 一: 1, 二: 2, 三: 3, 四: 4, 五: 5, 六: 6, 七: 7, 八: 8, 九: 9 }
  if (text === '十') return 10
  if (text.includes('十')) {
    const [a, b] = text.split('十')
    return (digits[a] || 1) * 10 + (digits[b] || 0)
  }
  return digits[text]
}

export function extractTimes(text) {
  // Book titles and citation metadata do not date the argument's subject.
  const body = text.replace(/《[^》]*》|\[\d+\]\([^)]*\)/g, ' ')
  const spans = [],
    occupied = []
  const add = (match, start, end, precision) => {
    spans.push({ start, end, label: match[0], precision, provenance: 'heuristic' })
    occupied.push([match.index, match.index + match[0].length])
  }
  const isOccupied = (match) =>
    occupied.some(([a, b]) => match.index < b && match.index + match[0].length > a)
  const isDuration = (match) =>
    /(?:持续|历时|长达|经过|已有|共计|第)\s*$/.test(body.slice(0, match.index)) ||
    /^(?:前|后|间|之久|时间)/.test(body.slice(match.index + match[0].length))
  for (const match of body.matchAll(
    /(公元前|公元)?\s*(\d{1,4})\s*年?\s*[—–\-至到~～]\s*(公元前|公元)?\s*(\d{1,4})\s*年/g,
  )) {
    const a = (match[1] === '公元前' ? -1 : 1) * Number(match[2])
    const b = (match[3] === '公元前' || (!match[3] && match[1] === '公元前') ? -1 : 1) * Number(match[4])
    if (a && b && (match[1] || Math.min(a, b) >= 100) && !isDuration(match))
      add(match, Math.min(a, b), Math.max(a, b), 'year')
  }
  for (const match of body.matchAll(/(公元前|公元)?\s*(\d{1,4})\s*年|[（(](\d{3,4})[)）]/g)) {
    if (isOccupied(match) || isDuration(match) || (!match[1] && !match[3] && Number(match[2]) < 100)) continue
    const year = Number(match[2] || match[3]) * (match[1] === '公元前' ? -1 : 1)
    if (year) add(match, year, year, 'year')
  }
  for (const match of body.matchAll(/(公元前)?([一二三四五六七八九十]{1,3}|\d{1,2})世纪/g)) {
    const century = numeric(match[2])
    if (!century) continue
    const start = match[1] ? -century * 100 : (century - 1) * 100 + 1
    const end = match[1] ? -(century - 1) * 100 - 1 : century * 100
    add(match, start, end, 'century')
  }
  const broadBody = periods
    .filter((entry) => entry[3] === 'approximate')
    .reduce((remaining, [term]) => remaining.replaceAll(term, ' '), body)
  for (const [term, start, end, precision = 'period'] of periods) {
    if ((precision === 'approximate' ? body : broadBody).includes(term))
      spans.push({ start, end, label: term, precision, provenance: 'heuristic' })
  }
  return [...new Map(spans.map((s) => [`${s.start}:${s.end}:${s.label}`, s])).values()]
}

export function timelineFor(document) {
  const units = new Map(
    document.units.map((unit) => {
      const spans = Array.isArray(unit.timeSpans) ? unit.timeSpans : extractTimes(unitText(unit, document))
      return [
        unit.id,
        spans.filter((s) => Number.isInteger(s?.start) && Number.isInteger(s?.end) && s.start <= s.end),
      ]
    }),
  )
  const spans = [...units.values()].flat()
  const domain = spans.length
    ? [Math.min(...spans.map((s) => s.start)), Math.max(...spans.map((s) => s.end))]
    : null
  return {
    units,
    domain,
    dated: [...units.values()].filter((s) => s.length).length,
    undated: [...units.values()].filter((s) => !s.length).length,
  }
}

export function unitsInTime(timeline, range) {
  if (!range) return null
  return new Set(
    [...timeline.units]
      .filter(([, spans]) => spans.some((s) => s.start <= range[1] && s.end >= range[0]))
      .map(([id]) => id),
  )
}

export function withParagraphs(document, unitIds) {
  if (!unitIds) return null
  return new Set([
    ...unitIds,
    ...(document?.units || []).filter((u) => unitIds.has(u.id)).map((u) => u.paragraphId),
  ])
}

export const formatYear = (year) => (year < 0 ? `前${Math.abs(year)}` : String(year))
